import type { CanvasObject, ConnectorCanvasObject } from "./canvas-object";
import { clampFrameToCanvas } from "./canvas-bounds";
import { resolveConnectorAppearance, type ConnectorPatchKey } from "./connector-appearance";
import {
  connectorStoredFrame,
  type ConnectorFrame,
  type ConnectorGeometryInput,
} from "./connector-geometry";

// コネクタ（接続矢印）の参照整合ロジック。クライアント（dispatcher / 貼り付け）と
// サーバ（WebSocket op-applier / REST ハンドラ）の双方から使い、整合ルールを一元化する。

/**
 * コネクタの端点になれる種別かどうか。ペンストローク・コネクタ自身・コメントスレッド（ピン）・
 * セクション（領域コンテナ。図形ではないため接続対象にしない）は端点にできない。
 * undefined（存在しない id）も不可。
 */
export function isConnectorEndpointObject(object: CanvasObject | undefined): object is CanvasObject {
  return (
    object !== undefined &&
    object.type !== "pen-stroke" &&
    object.type !== "connector" &&
    object.type !== "comment-thread" &&
    object.type !== "section"
  );
}

export type ConnectorNormalizeResult =
  | { readonly ok: true; readonly object: CanvasObject }
  | { readonly ok: false; readonly reason: string };

/**
 * 作成時にコネクタの参照整合を検証し、保存 bbox を端点から再計算（キャンバス内へ clamp）した
 * オブジェクトを返す。コネクタ以外はそのまま ok で返す。
 *
 * - 始点と終点が同一オブジェクトなら拒否（自己ループ禁止）。
 * - いずれかの端点が存在しない／端点にできない種別（ペン・コネクタ）なら拒否。
 * - 妥当なら、端点の現在位置から保存 bbox を再計算して上書きする（クライアントが任意の
 *   x/y/w/h を詰めて送っても、端点ルートと無関係な保存値を残さない）。
 *
 * objectsById には「作成後に存在するはずの全オブジェクト」を渡す
 * （サーバ DB の既存分 + 同一バッチ内の新規分など）。
 */
export function normalizeConnectorForCreate(
  object: CanvasObject,
  objectsById: ReadonlyMap<string, CanvasObject>
): ConnectorNormalizeResult {
  if (object.type !== "connector") return { ok: true, object };
  const { from, to } = object;
  if (from.objectId === to.objectId) {
    return { ok: false, reason: "コネクタの始点と終点が同一オブジェクトです。" };
  }
  const fromObj = objectsById.get(from.objectId);
  const toObj = objectsById.get(to.objectId);
  if (!isConnectorEndpointObject(fromObj) || !isConnectorEndpointObject(toObj)) {
    return {
      ok: false,
      reason: "コネクタの端点が存在しないか、接続できない種別（ペン / コネクタ）です。",
    };
  }
  const frame = computeConnectorStoredFrameFor(object, fromObj, toObj);
  return {
    ok: true,
    object: { ...object, x: frame.x, y: frame.y, w: frame.w, h: frame.h },
  };
}

/** 経路・描画の計算に渡すコネクタの項目（端点・見た目・線幅）。 */
export type ConnectorGeometrySource = Pick<
  ConnectorCanvasObject,
  "from" | "to" | "strokeWidth" | ConnectorPatchKey
>;

/**
 * コネクタと端点図形のフレームから、経路・描画の計算の入力を組み立てる。見た目は既定値まで解決する。
 * キャンバス・作成プレビュー・PDF・サムネイル・保存枠の計算が同じ入力を使うための入口。
 */
export function connectorGeometryInputFor(
  connector: ConnectorGeometrySource,
  fromFrame: ConnectorFrame,
  toFrame: ConnectorFrame
): ConnectorGeometryInput {
  const appearance = resolveConnectorAppearance(connector);
  return {
    fromFrame,
    fromSide: connector.from.side,
    toFrame,
    toSide: connector.to.side,
    lineType: appearance.lineType,
    linePattern: appearance.linePattern,
    route: appearance.route,
    startCap: appearance.startCap,
    endCap: appearance.endCap,
    ...(typeof connector.strokeWidth === "number" ? { strokeWidth: connector.strokeWidth } : {}),
  };
}

/** 図形のフレーム（回転を含む）。 */
export function connectorFrameOf(object: CanvasObject): ConnectorFrame {
  return { x: object.x, y: object.y, w: object.w, h: object.h, rotation: object.rotation ?? 0 };
}

/**
 * コネクタの保存枠（x / y / w / h）を、端点図形のフレームとコネクタの見た目から求める（キャンバス内の
 * 数値として正規化する）。端点の移動中のフレーム（確定前）から求めるクライアントの経路でも、サーバと
 * 同じ値になる。
 */
export function connectorStoredFrameForFrames(
  connector: ConnectorGeometrySource,
  fromFrame: ConnectorFrame,
  toFrame: ConnectorFrame
): { readonly x: number; readonly y: number; readonly w: number; readonly h: number } {
  const input = connectorGeometryInputFor(connector, fromFrame, toFrame);
  return clampFrameToCanvas(
    connectorStoredFrame(input.fromFrame, input.fromSide, input.toFrame, input.toSide, {
      lineType: input.lineType,
      route: input.route,
      startCap: input.startCap,
      endCap: input.endCap,
      ...(input.strokeWidth !== undefined ? { strokeWidth: input.strokeWidth } : {}),
    })
  );
}

/**
 * コネクタの保存枠（x / y / w / h）を、端点図形の現在位置とコネクタの見た目（線の形・経路・線幅・
 * 両端の形状）から求める（キャンバス内の数値として正規化する）。作成・経路や形状の更新・端点の
 * 移動に伴う再計算で、サーバとクライアントが同じ値を使う。
 */
export function computeConnectorStoredFrameFor(
  connector: ConnectorCanvasObject,
  fromObj: CanvasObject,
  toObj: CanvasObject
): { readonly x: number; readonly y: number; readonly w: number; readonly h: number } {
  return connectorStoredFrameForFrames(connector, connectorFrameOf(fromObj), connectorFrameOf(toObj));
}

/**
 * 指定 id 集合のいずれかを端点（from / to）に持つコネクタの id を返す。
 * 図形削除時に、その図形へ接続しているコネクタも巻き込んで削除するために使う。
 * 既に削除対象へ含まれているコネクタ自身は二重計上しない。
 */
export function collectConnectorCascadeDeleteIds(
  objects: Iterable<CanvasObject>,
  deletedIds: ReadonlySet<string>
): string[] {
  const result: string[] = [];
  for (const object of objects) {
    if (object.type !== "connector") continue;
    if (deletedIds.has(object.id)) continue;
    if (deletedIds.has(object.from.objectId) || deletedIds.has(object.to.objectId)) {
      result.push(object.id);
    }
  }
  return result;
}
